import React, { ReactNode, useEffect, useRef, useState } from "react";
import { BrandingConfigurationViewModel } from "./branding-configuration.view-model";
import { useAuth } from "../auth/auth-context";
import {
	IAlertContent,
	IFileInfo,
	IBusinessNumberModel,
	IBusinessModel,
	IIncomingCallSettings,
} from "src/types/models/apiModels";
import UploadButton from "src/components/molecules/upload-button/upload-button";
import InputBox from "src/components/atoms/v2/inputBox/inputBox";
import IPhone from "./iPhone/iPhone";
import MobileAlertNotification from "./mobile-alert-notification/mobile-alert-notification";
import { ReactComponent as IPhoneIcon } from "../../../assets/svg/apple.svg";
import { ReactComponent as AndroidIcon } from "../../../assets/svg/android.svg";
import AndroidPhone from "./androidPhone/androidPhone";
import IPhoneInApp from "./iPhone/iPhoneInApp";
import Alert from "src/components/atoms/alert/alert";
import NoAlert from "src/components/atoms/alert/no-alert";
import CallScreen from "./call-screen/call-screen";
import { defaultScreenSetting } from "src/models/default-screen-settings";

interface BrandingConfigurationProps {
	business: IBusinessModel;
	number: IBusinessNumberModel;
	onChange: (number: IBusinessNumberModel) => void;
	scProvidedNumber: boolean;
	showInAppModel?: boolean;
	showInAppBranding?: boolean;
	inboundCallSettings?: IIncomingCallSettings;
	appName?: string;
}

const BrandingConfiguration: React.FC<BrandingConfigurationProps> = ({
	business,
	number,
	scProvidedNumber,
	onChange,
	showInAppModel = false,
	showInAppBranding = false,
	inboundCallSettings,
	appName,
}) => {
	const defaultSetupAlertContent: IAlertContent = {
		title: business.name,
		body: "Incoming call scheduled",
	};
	const defaultCleanUpAlertContent: IAlertContent = {
		title: business.name,
		body: "Branding Cleared",
	};
	const { user } = useAuth(); // Extract user from useAuth hook
	const viewModel = useRef<BrandingConfigurationViewModel | null>(null);

	const [selectedImage, setSelectedImage] = useState<string>(
		number.branding?.imageFile?.signedUrl || business.logo.signedUrl || ""
	);
	const [selectedImageFile, setSelectedImageFile] = useState<IFileInfo>();
	const [setupAlertContent, setSetupAlertContent] = useState<IAlertContent>(
		number.branding?.setupPushContent || defaultSetupAlertContent
	);
	const [cleanupAlertContent, setCleanupAlertContent] =
		useState<IAlertContent>(
			number?.branding?.cleanupPushContent || defaultCleanUpAlertContent
		);
	const [defaultIntent, setDefaultIntent] = useState("Default Intent");
	const [defaultTTL, setDefaultTTL] = useState<number>(10);
	const [brandingName, setBrandingName] = useState(business.name);
	const [businessNumber, setBusinessNumber] = useState<
		IBusinessNumberModel | undefined
	>();
	const [isModified, setIsModified] = useState(false);

	useEffect(() => {
		if (!number.branding) {
			number.branding = {
				brandingName: business.name,
				defaultIntent: defaultIntent,
				defaultTTLInMinutes: defaultTTL,
				cleanupPushContent: defaultCleanUpAlertContent,
				setupPushContent: defaultSetupAlertContent,
				imageFile: {
					fileName: "defaultFile",
					signedUrl: selectedImage,
				},
			};
		}
	}, []);

	useEffect(() => {
		const fetchData = async () => {
			try {
				if (user && user.accessToken) {
					viewModel.current = new BrandingConfigurationViewModel(
						user.accessToken
					);
					const response = await viewModel.current?.fetchNumber(
						business.businessId,
						number.businessNumber
					);

					if (response) {
						const num: IBusinessNumberModel = {
							...response,

							managedNumberSettings: {
								cPaaSProvider:
									response.managedNumberSettings
										?.cPaaSProvider || "Vonage",
								screenSettings: showInAppModel
									? response.managedNumberSettings
											.screenSettings
									: undefined,
							},
							branding: {
								brandingName:
									response.branding?.brandingName ||
									number.branding.brandingName,
								cleanupPushContent:
									response.branding?.cleanupPushContent ||
									number.branding.cleanupPushContent,
								setupPushContent:
									response.branding?.setupPushContent ||
									number.branding.setupPushContent,
								defaultIntent:
									response.branding?.defaultIntent ||
									number.branding.defaultIntent,
								defaultTTLInMinutes:
									response.branding?.defaultTTLInMinutes ||
									number.branding.defaultTTLInMinutes,
								imageFile:
									response.branding?.imageFile ||
									number.branding.imageFile,
							},
							scProvidedNumber: scProvidedNumber,
						};

						setBusinessNumber(() => num);
						if (num.branding.setupPushContent) {
							setSetupAlertContent(
								() =>
									num.branding.setupPushContent ??
									defaultSetupAlertContent
							);
						}
						if (num.branding.cleanupPushContent) {
							setCleanupAlertContent(
								() =>
									num.branding.cleanupPushContent ??
									defaultCleanUpAlertContent
							);
						}

						setSelectedImage(
							() =>
								num.branding?.imageFile?.signedUrl ??
								selectedImage
						);
						setBrandingName(() => num.branding?.brandingName ?? "");
						setDefaultIntent(
							() => num.branding.defaultIntent ?? ""
						);
						setDefaultTTL(num.branding.defaultTTLInMinutes);
						num.managedNumberSettings =
							num.managedNumberSettings || {};

						// Assign default settings if screenSettings is undefined
						num.managedNumberSettings.screenSettings =
							num.managedNumberSettings.screenSettings ||
							defaultScreenSetting;

						onChange(num);
					}
				}
			} catch (error) {
				console.error("Error fetching data:", error);
			} finally {
			}
		};

		fetchData();
	}, [user]);

	useEffect(() => {
		if (onChange && businessNumber) {
			onChange(businessNumber);
		}
	}, [businessNumber]);

	useEffect(() => {
		if (businessNumber && selectedImageFile) {
			const updatedNumber: IBusinessNumberModel = {
				...businessNumber,
				branding: {
					brandingName: brandingName,
					imageFile: selectedImageFile,
					setupPushContent: setupAlertContent,
					cleanupPushContent: cleanupAlertContent,
					defaultIntent: defaultIntent,
					defaultTTLInMinutes: defaultTTL,
				},
			};

			setBusinessNumber(() => updatedNumber);
		}
	}, [
		setupAlertContent,
		cleanupAlertContent,
		defaultIntent,
		brandingName,
		selectedImageFile,
	]);

	const handleSetupAlertChange = (
		key: keyof IAlertContent,
		value: string
	) => {
		setSetupAlertContent((prevAlert: IAlertContent) => ({
			...prevAlert,
			[key]: value,
		}));

		markModified();
	};

	const handleCleanupAlertChange = (
		key: keyof IAlertContent,
		value: string
	) => {
		setCleanupAlertContent((prevAlert: IAlertContent) => ({
			...prevAlert,
			[key]: value,
		}));

		markModified();
	};

	const handleImageUpload = async (image: IFileInfo | undefined) => {
		if (image) {
			setSelectedImageFile(() => image);
		}

		if (number.branding.imageFile) {
			number.branding.imageFile.signedUrl = image?.signedUrl;
			number.branding.imageFile.fileName = image?.fileName;
		}

		setSelectedImage(image?.signedUrl ?? selectedImage);
		markModified();
	};

	const markModified = () => {
		number.hasUnsavedChanges = true;
		setIsModified(true);
		onChange(number);
	};

	const phoneLabel = (icon: ReactNode, title: string) => (
		<div className="flex items-center justify-center gap-2 bg-white p-2 px-4 drop-shadow-md border-b-4 border-orange-500">
			{icon}
			<label className="font-semibold text-lg text-gray-900">
				{title}
			</label>
		</div>
	);

	return (
		<>
			<div className="bg-gradient-to-br from-gray-100 to-gray-300 flex flex-col items-center justify-between gap-4 p-6 h-full w-full rounded-lg">
				<div className="flex items-center justify-between gap-12 w-full h-full overflow-y-auto">
					<div className="flex flex-col items-center justify-start gap-4 h-full text-gray-800 w-full overflow-y-auto">
						<div className="bg-gradient-to-r from-sky-900 to-sky-700 w-full flex items-center justify-start gap-2 p-4 drop-shadow-lg border-b-4 border-orange-500">
							<span className="font-Poppins font-semibold text-2xl text-white">
								Call Branding
							</span>
							<span className="font-Poppins font-regular text-md text-white">
								(calls from contact center to mobile phone)
							</span>
						</div>
						{/* Alert */}
						<div className="drop-shadow-md w-full">
							<Alert
								text={
									"This is just a sneak peek of how your branding might look! While we’ve done our best to keep it realistic, the actual appearance may look slightly different depending on your device."
								}
								type={"info"}
							></Alert>
						</div>
						{/* Alert */}
						{/* Edit */}
						<div className="bg-white w-full flex items-start gap-2 p-4 drop-shadow-lg">
							<div className="flex items-center justify-center flex-shrink-0 p-4 h-full drop-shadow-lg">
								<UploadButton
									defaultImage={selectedImage}
									onUploadSuccess={handleImageUpload}
									borderRadius="50%"
									buttonType="Image"
									addTrustShield
									showBorder={false}
								/>
							</div>
							<div className="flex flex-col items-center justify-center gap-2 w-full">
								<div className=" flex  items-center justify-center gap-2 w-full">
									<div className="bg-slate-50 w-full flex flex-col gap-4 h-full">
										<span className="bg-gradient-to-r from-sky-700 to-sky-900 font-semibold w-full h6 uppercase text-center text-white p-2 drop-shadow-md border-b-4 border-orange-500">
											Incoming call alert
										</span>
										<div className="flex flex-col items-center justify-center gap-4 h-full p-4">
											<InputBox
												value={setupAlertContent?.title}
												onChange={(value) => {
													if (
														!number.branding
															.setupPushContent
													) {
														number.branding.setupPushContent =
															{
																title: "",
																body: "",
															};
													} else {
														number.branding.setupPushContent.title =
															value;
													}
													handleSetupAlertChange(
														"title",
														value
													);
												}}
												label="Title"
												type={"text"}
												required
											></InputBox>
											<InputBox
												value={setupAlertContent?.body}
												onChange={(value) => {
													if (
														!number.branding
															.setupPushContent
													) {
														number.branding.setupPushContent =
															{
																title: "",
																body: "",
															};
													} else {
														number.branding.setupPushContent.body =
															value;
													}
													handleSetupAlertChange(
														"body",
														value
													);
												}}
												label="Body"
												type={"text"}
												required
											></InputBox>
										</div>
									</div>
									<div className="bg-slate-50 w-full flex flex-col gap-4 h-full">
										<span className="bg-gradient-to-r from-sky-700 to-sky-900 font-semibold w-full h6 uppercase text-center text-white p-2 drop-shadow-md border-b-4 border-orange-500">
											BRANDING CLEAR ALERT
										</span>
										<div className="flex flex-col items-center justify-center gap-4 h-full p-4">
											<InputBox
												value={
													cleanupAlertContent?.title
												}
												onChange={(value) => {
													if (
														!number.branding
															.cleanupPushContent
													) {
														number.branding.cleanupPushContent =
															{
																title: "",
																body: "",
															};
													} else {
														number.branding.cleanupPushContent.title =
															value;
													}
													handleCleanupAlertChange(
														"title",
														value
													);
												}}
												label="Title"
												type={"text"}
												required
											></InputBox>
											<InputBox
												value={
													cleanupAlertContent?.body
												}
												onChange={(value) => {
													if (
														!number.branding
															.cleanupPushContent
													) {
														number.branding.cleanupPushContent =
															{
																title: "",
																body: "",
															};
													} else {
														number.branding.cleanupPushContent.body =
															value;
													}
													handleCleanupAlertChange(
														"body",
														value
													);
												}}
												label="Body"
												type={"text"}
												required
											></InputBox>
										</div>
									</div>
									<div className="bg-slate-50 w-full flex flex-col gap-4 h-full">
										<span className="bg-gradient-to-r from-sky-700 to-sky-900 font-semibold w-full h6 uppercase text-center text-white p-2 drop-shadow-md border-b-4 border-orange-500">
											CALL SCREEN
										</span>
										<div className="flex flex-col items-center justify-center gap-4 h-full p-4">
											<InputBox
												value={brandingName}
												onChange={(value) => {
													setBrandingName(value);
													number.branding.brandingName =
														value;
													markModified();
												}}
												label="Branding Name"
												type={"text"}
												required
												maxLength={50}
											></InputBox>
											<InputBox
												value={defaultIntent}
												onChange={(value) => {
													setDefaultIntent(value);
													number.branding.defaultIntent =
														value;
													markModified();
												}}
												label="Default Intent"
												type={"text"}
												required
											></InputBox>
										</div>
									</div>
								</div>
								<div className="bg-slate-50 flex flex-col justify-center items-center gap-4 w-full p-4">
									<InputBox
										label={"Default TTL in minutes"}
										type={"text"}
										required
										value={`${defaultTTL ?? ""}`}
										onChange={(value: string): void => {
											// Parse the input value
											if (value === "") {
												setDefaultTTL(10); // Update state with empty string
												markModified();
												return; // Exit without validation
											}

											const parsedValue =
												Number.parseInt(value);

											// Check if parsedValue is a valid number
											if (isNaN(parsedValue)) {
												return; // Reject the update
											}

											// Check if the value exceeds the max limit (24 hours = 1440 minutes)
											if (parsedValue > 1440) {
												return;
											}

											// Update the state if valid
											setDefaultTTL(parsedValue); // Set the parsed value as number
											number.branding.defaultTTLInMinutes =
												parsedValue;
											markModified();
										}}
										helpText="Max 1440 minutes i.e. 24 hours"
									></InputBox>

									{/* <span className="font-Poppins font-semibold text-sm">
										{defaultTTL
											? (defaultTTL / 60).toFixed(2) // Convert to hours and show 2 decimal places
											: "0"}{" "}
										hours
									</span> */}
								</div>
							</div>
						</div>
						{/* Edit */}

						{/* Phones */}
						<div className="flex items-center gap-4 h-full p-4">
							{/* iPhone */}
							<div className="flex flex-col items-center justify-center gap-4">
								<MobileAlertNotification
									appImage={selectedImage}
									title={setupAlertContent.title}
									body={setupAlertContent.body}
								></MobileAlertNotification>
								{!showInAppModel && (
									<MobileAlertNotification
										appImage={selectedImage}
										title={cleanupAlertContent.title}
										body={cleanupAlertContent.body}
									></MobileAlertNotification>
								)}
								<IPhone
									brandingImage={selectedImage}
									businessName={brandingName}
									intent={defaultIntent}
								></IPhone>
								{phoneLabel(
									<IPhoneIcon className="w-6 h-6"></IPhoneIcon>,
									"iPhone (PSTN)"
								)}
							</div>
							{/* iPhone */}
							{showInAppModel && showInAppBranding && (
								<div className="flex flex-col items-center justify-center gap-4">
									<MobileAlertNotification
										appImage={selectedImage}
										title={cleanupAlertContent.title}
										body={cleanupAlertContent.body}
									></MobileAlertNotification>
									<IPhoneInApp
										brandingImage={selectedImage}
										businessName={brandingName}
										intent={defaultIntent}
										appName={appName ?? ""}
									></IPhoneInApp>
									{phoneLabel(
										<IPhoneIcon className="w-6 h-6"></IPhoneIcon>,
										"iPhone (In-App)"
									)}
								</div>
							)}
							{/* Android */}
							{showInAppModel && (
								<div className="flex flex-col items-center justify-center gap-4">
									<NoAlert></NoAlert>
									<AndroidPhone
										brandingImage={selectedImage}
										businessName={brandingName}
										intent={defaultIntent}
										phoneNumber={number.businessNumber}
									></AndroidPhone>
									{phoneLabel(
										<AndroidIcon className="w-6 h-6"></AndroidIcon>,
										"Android (PSTN)"
									)}
								</div>
							)}
							{/* Android */}
							{showInAppBranding && (
								<div className="flex flex-col items-center justify-center gap-4">
									{!showInAppModel && showInAppBranding && (
										<NoAlert></NoAlert>
									)}
									<NoAlert></NoAlert>
									<AndroidPhone
										brandingImage={selectedImage}
										businessName={brandingName}
										intent={defaultIntent}
										phoneNumber={number.businessNumber}
									></AndroidPhone>
									{phoneLabel(
										<AndroidIcon className="w-6 h-6"></AndroidIcon>,
										"Android (In-App)"
									)}
								</div>
							)}
						</div>
						{inboundCallSettings && (
							<div className="w-full flex flex-col items-center justify-center gap-4 p-4">
								<div className="bg-gradient-to-r from-sky-900 to-sky-700 w-full flex items-center justify-start gap-2 p-4 drop-shadow-lg border-b-4 border-orange-500">
									<span className="font-Poppins font-semibold text-2xl text-white">
										Incoming call settings
									</span>
									<span className="font-Poppins font-regular text-md text-white">
										(calls from mobile to contact center)
									</span>
								</div>
								<div className="flex flex-col items-center justify-center gap-4">
									<span className="font-Poppins font-semibold text-md uppercase">
										Call screen UI
									</span>
									<CallScreen
										brandingImage={selectedImage}
										businessName={brandingName}
										intent={defaultIntent}
										currentScreenSettings={(() => {
											// Initialize managedNumberSettings if undefined
											number.managedNumberSettings =
												number.managedNumberSettings ||
												{};

											// Assign default settings if screenSettings is undefined
											number.managedNumberSettings.screenSettings =
												number.managedNumberSettings
													.screenSettings ||
												defaultScreenSetting;

											// Return the screen settings
											return number.managedNumberSettings
												.screenSettings;
										})()}
										onChange={(settings) => {
											// Ensure managedNumberSettings is initialized
											number.managedNumberSettings =
												number.managedNumberSettings ||
												{};
											// Assign the screenSettings
											number.managedNumberSettings.screenSettings =
												settings;
											markModified();
										}}
									></CallScreen>

									<div className="p-1 rounded-md drop-shadow-md">
										<span className="bg-sky-500 font-Poppins font-semibold text-sm text-white border-r-2 border-gray-800 px-2 p-1">
											SIP URI
										</span>
										<span className="bg-white font-Poppins font-semibold text-sm px-2 p-1">{`sip:${number.businessNumber}@${inboundCallSettings?.sipEndpointUrl}`}</span>
									</div>
								</div>
							</div>
						)}
						{/* Phones */}
					</div>
				</div>
			</div>
		</>
	);
};

export default BrandingConfiguration;
